import { useState } from "react";
import type { FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { adminApi, eventsApi } from "@/lib/api/endpoints";
import { useAsync } from "@/lib/hooks/useAsync";
import {
  Badge, Button, Card, EmptyState, Field, Input, Modal, Select, useToast,
} from "@/components/ui";
import { formatRelative, slugify, truncate } from "@/lib/utils/format";
import type { EventType } from "@/lib/types/config";
import type { DashboardPayload, RecentGeneration } from "@/lib/types/api";
import { EventCard } from "./components/EventCard";
import { IconPlus, IconSparkles } from "./components/icons";

export function DashboardPage() {
  const dashboard = useAsync(() => adminApi.dashboard(), []);
  const events = useAsync(() => eventsApi.list(), []);
  const [creating, setCreating] = useState(false);

  const org = dashboard.data?.organization;
  const used = org?.generations_used ?? 0;
  const limit = org?.generations_limit ?? 1000;
  const percent = limit > 0 ? Math.min(100, Math.round((used / limit) * 100)) : 0;

  const activeEvents = (events.data ?? []).filter((event) => event.status !== "archived");

  return (
    <div className="flex flex-col gap-8">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-2">
          <span className="label-eyebrow text-accent">Workspace overview</span>
          <h1 className="text-3xl font-bold tracking-tight text-haze-100 sm:text-4xl">Your events, at a glance.</h1>
          <p className="max-w-2xl text-sm leading-relaxed text-haze-300">Create memorable booth experiences, keep an eye on activity, and get your next event ready to share.</p>
        </div>
        <span className="inline-flex items-center gap-2 rounded-full border border-accent/20 bg-accent/5 px-3.5 py-2 text-xs font-medium text-accent">
          <span className="h-2 w-2 rounded-full bg-accent" /> JebaXP workspace
        </span>
      </header>

      <div className="grid gap-5 lg:grid-cols-[1fr_360px]">
        <Card className="relative flex flex-col gap-5 overflow-hidden p-6 sm:p-7">
          <div aria-hidden className="pointer-events-none absolute -right-16 -top-24 h-64 w-64 rounded-full bg-accent/[0.08] blur-3xl" />
          <span className="label-eyebrow relative">Monthly generations quota</span>
          <div className="flex items-end gap-2">
            <span className="text-5xl font-bold leading-none tabular-nums tracking-tight">{used.toLocaleString()}</span>
            <span className="pb-0.5 text-sm text-haze-400">/ {limit.toLocaleString()} generations used</span>
          </div>

          <div className="flex flex-col gap-2">
            <div className="h-1.5 overflow-hidden rounded-full bg-ink-600">
              <div
                className="h-full rounded-full bg-gradient-to-r from-aqua to-accent transition-[width] duration-500"
                style={{ width: `${percent}%` }}
              />
            </div>
            <div className="flex justify-between text-2xs uppercase tracking-wider text-haze-400">
              <span>{percent}% consumed</span>
              <span>Resets monthly</span>
            </div>
          </div>

          {dashboard.data && <HealthStrip data={dashboard.data} />}
        </Card>

        <Card className="flex flex-col gap-5 p-6 sm:p-7">
          <span className="label-eyebrow">Events summary</span>
          <div className="grid grid-cols-3 gap-2 text-center">
            <Stat value={dashboard.data?.stats.active_events ?? 0} label="Active" tone="text-aqua" />
            <Stat value={dashboard.data?.stats.draft_events ?? 0} label="Draft" tone="text-accent" />
            <Stat value={dashboard.data?.stats.archived_events ?? 0} label="Archived" tone="text-haze-300" />
          </div>
          <Button variant="primary" size="lg" fullWidth icon={<IconPlus size={16} />} onClick={() => setCreating(true)}>
            Create New Event
          </Button>
        </Card>
      </div>

      <section className="flex flex-col gap-4">
        <h2 className="label-eyebrow">Your events</h2>

        {events.loading ? (
          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            {Array.from({ length: 3 }).map((_, index) => <div key={index} className="skeleton h-52" />)}
          </div>
        ) : activeEvents.length === 0 ? (
          <EmptyState
            icon={<IconSparkles size={28} />}
            title="No events yet"
            description="Create an event to get a booth link you can open on a kiosk."
            action={<Button variant="primary" onClick={() => setCreating(true)}>Create your first event</Button>}
          />
        ) : (
          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
            {activeEvents.map((event) => (
              <EventCard key={event.id} event={event} onChanged={events.reload} />
            ))}
          </div>
        )}
      </section>

      <section className="flex flex-col gap-4">
        <h2 className="label-eyebrow">Recent booth activity</h2>
        <RecentActivity items={dashboard.data?.recent_generations ?? []} loading={dashboard.loading} />
      </section>

      <CreateEventModal open={creating} onClose={() => setCreating(false)} onCreated={events.reload} />
    </div>
  );
}

function Stat({ value, label, tone }: { value: number; label: string; tone: string }) {
  return (
    <div className="flex flex-col gap-1">
      <span className={`text-3xl font-bold leading-none tabular-nums ${tone}`}>{value}</span>
      <span className="text-2xs uppercase tracking-wider text-haze-400">{label}</span>
    </div>
  );
}

/** Live operational read: what an operator checks mid-event. */
function HealthStrip({ data }: { data: DashboardPayload }) {
  const { generation_health: health } = data;
  const printFailures = (data.print_health.failed ?? 0) + (data.print_health.expired ?? 0);

  return (
    <div className="flex flex-wrap items-center gap-2 border-t border-ink-600 pt-4">
      <span className="text-2xs uppercase tracking-wider text-haze-400">Last 24h</span>
      <Badge tone="neutral">{health.last_24h} generated</Badge>
      {health.in_flight > 0 && <Badge tone="aqua">{health.in_flight} in flight</Badge>}
      {health.failed > 0 && <Badge tone="danger">{health.failed} failed</Badge>}
      {printFailures > 0 && <Badge tone="warn">{printFailures} print issues</Badge>}
      {health.failed === 0 && printFailures === 0 && health.last_24h > 0 && <Badge tone="ok">All healthy</Badge>}
    </div>
  );
}

function RecentActivity({ items, loading }: { items: RecentGeneration[]; loading: boolean }) {
  if (loading) return <div className="skeleton h-40" />;
  if (items.length === 0) {
    return <EmptyState title="No generations yet" description="Booth activity will appear here once guests start creating." />;
  }

  return (
    <Card className="overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-ink-600 text-left">
            {["Image preview", "Event", "Provider", "Prompt used", "Time"].map((heading) => (
              <th key={heading} className="label-eyebrow px-5 py-3 font-semibold">{heading}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.id} className="border-b border-ink-600/60 last:border-0 hover:bg-ink-700/40">
              <td className="px-5 py-3">
                {item.image_url ? (
                  <img src={item.image_url} alt="" className="h-11 w-11 rounded-md border border-ink-600 object-cover" />
                ) : (
                  <div className="h-11 w-11 rounded-md border border-ink-600 bg-ink-800" />
                )}
              </td>
              <td className="px-5 py-3 text-haze-100">{item.event_name}</td>
              <td className="px-5 py-3">
                <code className="rounded bg-ink-800 px-1.5 py-0.5 text-xs text-aqua">{item.provider ?? "—"}</code>
              </td>
              <td className="max-w-md px-5 py-3 text-haze-300">{truncate(item.prompt_used, 70) || "—"}</td>
              <td className="whitespace-nowrap px-5 py-3 text-haze-400">{formatRelative(item.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function CreateEventModal({
  open, onClose, onCreated,
}: { open: boolean; onClose: () => void; onCreated: () => void }) {
  const navigate = useNavigate();
  const toast = useToast();
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [eventType, setEventType] = useState<EventType>("other");
  const [submitting, setSubmitting] = useState(false);

  const effectiveSlug = slugTouched ? slug : slugify(name);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    try {
      const created = await eventsApi.create({
        name,
        slug: effectiveSlug,
        event_type: eventType,
        status: "draft",
        ai_photo_booth: true,
        photo_booth: true,
        print_photo: false,
      });
      toast.success(`${created.name} created`);
      onCreated();
      onClose();
      navigate(`/admin/events/${created.id}/builder`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not create that event");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Create a new event"
      description="You can change everything later in the Site Builder. The slug becomes the booth's public link."
    >
      <form onSubmit={submit} className="flex flex-col gap-4">
        <Field label="Event name" required htmlFor="event-name">
          <Input
            id="event-name"
            required
            autoFocus
            placeholder="Negus Malt"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </Field>

        <Field label="Slug" required hint={`Booth link: ${window.location.origin}/${effectiveSlug || "your-event"}`}>
          <Input
            required
            spellCheck={false}
            className="font-mono"
            placeholder="negus-malt"
            value={effectiveSlug}
            onChange={(event) => { setSlugTouched(true); setSlug(slugify(event.target.value)); }}
          />
        </Field>

        <Field label="Event type">
          <Select value={eventType} onChange={(event) => setEventType(event.target.value as EventType)}>
            <option value="festival">Festival</option>
            <option value="sports">Sports</option>
            <option value="wedding">Wedding</option>
            <option value="corporate">Corporate</option>
            <option value="other">Other</option>
          </Select>
        </Field>

        <div className="mt-2 flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} type="button">Cancel</Button>
          <Button variant="primary" type="submit" loading={submitting} disabled={!name || !effectiveSlug}>
            Create event
          </Button>
        </div>
      </form>
    </Modal>
  );
}
